import React from 'react';
import { motion } from 'framer-motion';
import {
  Wallet,
  MessageSquare,
  TrendingUp,
  Award,
  Sparkles,
  Zap,
  Lightbulb,
  Target,
} from 'lucide-react';
import { Container } from '../ui';

/*
 * WhyChooseUsSection
 * ------------------
 * "Why teams choose Istatec" — a grid of concrete reasons a prospective
 * client should engage with us, deliberately written in a direct,
 * team-voiced tone (no "leverage / empower / unlock" marketing fluff).
 * The point is to give a visitor real signals — pricing, engagement
 * style, track record — right before they submit the contact form.
 *
 * Rendered inline on the Contact page (just above the form) and
 * available as an export for any other page that wants to remind a
 * visitor why the engagement is worth starting.
 */

interface Reason {
  icon: React.ElementType;
  title: string;
  description: string;
}

const REASONS: Reason[] = [
  {
    icon: Wallet,
    title: 'Competitive pricing',
    description:
      'Right-sized for the value delivered — transparent quotes, no bloated retainers, and no surprise line-items at the end of the quarter.',
  },
  {
    icon: MessageSquare,
    title: 'Direct client interaction',
    description:
      "You talk to the people actually building your product — architects, engineers, designers — not layers of account managers relaying messages.",
  },
  {
    icon: TrendingUp,
    title: 'Scalable teams',
    description:
      'From a single senior advisor to a full delivery squad. Pods flex up or down with your roadmap, so you never pay for capacity you\u2019re not using.',
  },
  {
    icon: Award,
    title: '>98% customer satisfaction',
    description:
      "Our CSAT score is one of the reasons clients keep coming back for their next project — and refer us to the ones after that.",
  },
  {
    icon: Sparkles,
    title: 'Best-sourced expert engineers',
    description:
      "Handpicked, senior, and vetted for the work — not the lowest-cost bench. The team you meet is the team that ships.",
  },
  {
    icon: Zap,
    title: 'First slice in weeks',
    description:
      "Working prototype in front of your team within weeks, not quarters. We\u2019d rather show than tell — working software beats a proposal deck.",
  },
  {
    icon: Lightbulb,
    title: 'Continuous learning & R&D',
    description:
      "The team meets regularly to stay current with new frameworks, models, and patterns — and the useful ones filter into what we build for you.",
  },
  {
    icon: Target,
    title: 'Mid-scale focus',
    description:
      "Your project matters here. We\u2019re deliberately mid-scale, so you\u2019re not one of 10,000 line-items on a global P&L.",
  },
];

// Section heading + optional subtitle. Extracted as props so the same
// section can be dropped into different contexts (Contact page vs.
// About page CTAs) with slightly different framing.
export interface WhyChooseUsSectionProps {
  heading?: string;
  subheading?: string;
  className?: string;
}

export const WhyChooseUsSection: React.FC<WhyChooseUsSectionProps> = ({
  heading = 'Why teams choose Istatec',
  subheading = "Before you fill out the form below, here\u2019s what you\u2019re actually signing up for — the things clients tell us matter most when they pick us over the alternatives.",
  className,
}) => (
  <section className={`bg-white py-16 md:py-20 lg:py-24 ${className ?? ''}`}>
    <Container>
      <motion.h2
        className="heading-lg text-navy-900 mb-4"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        {heading}
      </motion.h2>
      {subheading && (
        <motion.p
          className="max-w-3xl text-base md:text-lg text-navy-600 leading-relaxed mb-12 md:mb-14"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          {subheading}
        </motion.p>
      )}

      {/*
        4-column grid on lg+ so all 8 reasons live in a tidy 4x2. Two
        columns on md, single column on mobile. `gap-y-10` gives each
        tile enough vertical breathing room to read as its own idea.
       */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 lg:gap-x-8 gap-y-10 md:gap-y-12">
        {REASONS.map((reason, i) => (
          <ReasonTile
            key={reason.title}
            reason={reason}
            animationDelay={Math.min(i, 5) * 0.05}
          />
        ))}
      </div>
    </Container>
  </section>
);

const ReasonTile: React.FC<{ reason: Reason; animationDelay: number }> = ({
  reason,
  animationDelay,
}) => {
  const Icon = reason.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{
        duration: 0.45,
        delay: animationDelay,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {/* Editorial treatment: no boxed card, no drop shadow — just an
          accent icon chip, a bold heading, and a short body. Feels
          designed rather than dumped into a generic template. */}
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-accent-50 text-accent-600 mb-4">
        <Icon className="w-5 h-5" aria-hidden />
      </div>
      <h3 className="text-lg md:text-[19px] font-bold text-navy-900 mb-2">
        {reason.title}
      </h3>
      <p className="text-[14.5px] md:text-[15px] text-navy-600 leading-relaxed">
        {reason.description}
      </p>
    </motion.div>
  );
};
